import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  adjustBalance,
  getCustomer,
  getCustomerActivity,
  getCustomerTimeline,
  grantPremium,
  listCustomers,
  setDeveloper,
  updateCustomer,
} from "./api";
import type {
  ActivityPeriod,
  BalanceAdjustBody,
  CustomerListParams,
  CustomerUpdate,
  DeveloperToggleBody,
  GrantPremiumBody,
} from "./types";

const keys = {
  list: (p: CustomerListParams) => ["customers", "list", p] as const,
  detail: (id: number) => ["customers", "detail", id] as const,
  timeline: (id: number) => ["customers", "timeline", id] as const,
  activity: (id: number, period: ActivityPeriod) => ["customers", "activity", id, period] as const,
};

export function useCustomers(params: CustomerListParams) {
  return useQuery({
    queryKey: keys.list(params),
    queryFn: () => listCustomers(params),
    placeholderData: (prev) => prev, // keep table mounted across page/filter changes
  });
}

export function useCustomerDetail(id: number | null) {
  return useQuery({
    queryKey: id !== null ? keys.detail(id) : ["customers", "detail", "none"],
    queryFn: () => getCustomer(id as number),
    enabled: id !== null,
  });
}

export function useCustomerTimeline(id: number | null) {
  return useQuery({
    queryKey: id !== null ? keys.timeline(id) : ["customers", "timeline", "none"],
    queryFn: () => getCustomerTimeline(id as number),
    enabled: id !== null,
  });
}

// Paged activity feed; `fetchNextPage` backs the "load more" button for 7d / all.
export function useCustomerActivity(id: number | null, period: ActivityPeriod) {
  return useInfiniteQuery({
    queryKey: id !== null ? keys.activity(id, period) : ["customers", "activity", "none", period],
    queryFn: ({ pageParam }) => getCustomerActivity(id as number, period, pageParam),
    initialPageParam: 1,
    getNextPageParam: (last, pages) => (last.next ? pages.length + 1 : undefined),
    enabled: id !== null,
  });
}

export function useUpdateCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: number; patch: CustomerUpdate }) =>
      updateCustomer(id, patch),
    onSuccess: (data, { id }) => {
      // Seed the detail with the PATCH response first, so the drawer re-locks on the saved
      // values without a stale flash; then refresh the list and this customer's detail.
      qc.setQueryData(keys.detail(id), data);
      qc.invalidateQueries({ queryKey: ["customers", "list"] });
      qc.invalidateQueries({ queryKey: keys.detail(id) });
    },
  });
}

// Shared invalidation after a sensitive op: the row's balance/premium/developer
// state changed, so both the detail and the list (loans_count etc.) must refresh.
function invalidateCustomer(qc: ReturnType<typeof useQueryClient>, id: number) {
  qc.invalidateQueries({ queryKey: ["customers", "list"] });
  qc.invalidateQueries({ queryKey: keys.detail(id) });
}

export function useSetDeveloper() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: DeveloperToggleBody }) =>
      setDeveloper(id, body),
    onSuccess: (_data, { id }) => invalidateCustomer(qc, id),
  });
}

export function useAdjustBalance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: BalanceAdjustBody }) =>
      adjustBalance(id, body),
    onSuccess: (_data, { id }) => invalidateCustomer(qc, id),
  });
}

export function useGrantPremium() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: GrantPremiumBody }) =>
      grantPremium(id, body),
    onSuccess: (_data, { id }) => invalidateCustomer(qc, id),
  });
}
